import { useState } from 'react';

import { useGetApplication } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconArrowLeft } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import TopUpStatusMessage from './TopUpStatusMessage';
import messages from './TopUpStatusMessages.messages';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import DrawdownPanel from '../../DrawdownPanel/DrawdownPanel';
import { TopUpForm } from '../../TopUpForm/TopUpForm';
import styles from '../RequestMoreFundsPanel.module.css';

type TOptions = 'none_selected' | 'drawdown' | 'request_more_funds';

export const PreApproved = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const {
        data: accountsResponse,
        isLoading: accountsLoading,
        isError: accountsError
    } = useQuery(getAccountsQueryConfig(stateKey));
    const [selectedOption, setSelectedOption] =
        useState<TOptions>('none_selected');

    const {
        data: applicationResponse,
        isLoading: applicationLoading,
        isError: applicationError
    } = useGetApplication(stateKey);

    if (
        accountsLoading ||
        applicationLoading ||
        accountsError ||
        applicationError
    )
        return null;

    const account = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const approvalAmount = account.latest_formal_offer?.available_cash;
    const expiryDate = account.latest_formal_offer?.timeout;

    const applicationDeclined = applicationResponse!.status === 'declined';

    switch (selectedOption) {
        case 'drawdown':
            return (
                <>
                    <BackButton setSelectedOption={setSelectedOption} />
                    <DrawdownPanel accountId={accountId} />
                </>
            );
        case 'request_more_funds':
            return (
                <>
                    {!applicationDeclined && (
                        <BackButton setSelectedOption={setSelectedOption} />
                    )}
                    <TopUpForm
                        accountId={accountId}
                        formHeader={
                            <div className={styles.formHeader}>
                                <h2 className={styles.heading}>
                                    <FormattedMessage
                                        {...messages.preApprovedMoreFundsCTA}
                                    />
                                </h2>
                                <p className={styles.description}>
                                    <FormattedMessage
                                        {...messages.requestMoreFundsHeading}
                                        values={{
                                            amount: (
                                                <strong>
                                                    <FormattedCurrency
                                                        value={approvalAmount!}
                                                    />
                                                </strong>
                                            )
                                        }}
                                    />
                                </p>
                            </div>
                        }
                    />
                </>
            );
        default:
            return (
                <>
                    <TopUpStatusMessage
                        heading={
                            <FormattedMessage
                                {...messages.preApprovedHeading}
                            />
                        }
                        description={
                            <FormattedMessage
                                {...messages.preApprovedDescription}
                                values={{
                                    amount: (
                                        <strong>
                                            <FormattedCurrency
                                                value={approvalAmount!}
                                            />
                                        </strong>
                                    ),
                                    date: (
                                        <strong>
                                            <FormattedDate
                                                value={new Date(expiryDate!)}
                                                day="2-digit"
                                                month="short"
                                                year="numeric"
                                            />
                                        </strong>
                                    )
                                }}
                            />
                        }
                    />
                    <div className={styles.extraContentContainer}>
                        <Button
                            variant="success"
                            className={styles.cta}
                            onClick={() => setSelectedOption('drawdown')}
                        >
                            <FormattedMessage {...messages.preApprovedCTA} />
                        </Button>
                        {!applicationDeclined && (
                            <div className={styles.extraContent}>
                                <p className={styles.extraContentDescription}>
                                    <FormattedMessage
                                        {...messages.preApprovedMoreFunds}
                                        values={{
                                            amount: (
                                                <strong>
                                                    <FormattedCurrency
                                                        value={approvalAmount!}
                                                    />
                                                </strong>
                                            )
                                        }}
                                    />
                                </p>
                                <Button
                                    variant="tertiary"
                                    onClick={() =>
                                        setSelectedOption('request_more_funds')
                                    }
                                >
                                    <FormattedMessage
                                        {...messages.preApprovedMoreFundsCTA}
                                    />
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            );
    }
};

const BackButton = ({
    setSelectedOption
}: {
    setSelectedOption: React.Dispatch<React.SetStateAction<TOptions>>;
}) => {
    return (
        <Button
            variant="tertiary"
            onClick={() => setSelectedOption('none_selected')}
            className={styles.backButton}
        >
            <IconArrowLeft />
            <FormattedMessage {...messages.backButtonCTA} />
        </Button>
    );
};
